import React, { useState, useEffect, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';

const navyLogo = 'https://BelvoirCastle.b-cdn.net/Logos/navy_logo_stacked.webp';

// ─── Brand tokens ─────────────────────────────────────────────────────────────
const NAVY   = '#101d40';
const BRONZE = '#9d6d49';
const CREAM  = '#f8f7f4';
const RED    = '#b94040';

// ─── Keyframes ────────────────────────────────────────────────────────────────
const fadeIn = keyframes`from{opacity:0}to{opacity:1}`;
const fadeOut = keyframes`from{opacity:1}to{opacity:0}`;
const slideUp = keyframes`
  from { opacity: 0; transform: translateY(22px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const lineFill  = keyframes`from{width:0}to{width:100%}`;
const softPulse = keyframes`0%,100%{opacity:1}50%{opacity:0.38}`;
const dotPulse  = keyframes`0%,100%{transform:scale(1);opacity:1}50%{transform:scale(1.4);opacity:0.6}`;
const checkDraw = keyframes`from{stroke-dashoffset:60}to{stroke-dashoffset:0}`;
const popIn = keyframes`
  0%   { transform: scale(0.88) translateY(10px); opacity: 0; }
  65%  { transform: scale(1.03) translateY(-2px); opacity: 1; }
  100% { transform: scale(1) translateY(0); opacity: 1; }
`;
const timerUrgent = keyframes`0%,100%{opacity:1}50%{opacity:0.55}`;

// Walking figure keyframes
const legFwd = keyframes`0%,100%{transform:rotate(30deg)}50%{transform:rotate(-30deg)}`;
const legBck = keyframes`0%,100%{transform:rotate(-30deg)}50%{transform:rotate(30deg)}`;
const armFwd = keyframes`0%,100%{transform:rotate(-24deg)}50%{transform:rotate(24deg)}`;
const armBck = keyframes`0%,100%{transform:rotate(24deg)}50%{transform:rotate(-24deg)}`;
const bodyBob = keyframes`0%,100%{transform:translateY(0)}50%{transform:translateY(1.5px)}`;

// ─── Layout ───────────────────────────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10000;
  background: ${CREAM};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  animation: ${({ $leaving }) =>
    $leaving
      ? css`${fadeOut} 0.5s ease forwards`
      : css`${fadeIn} 0.35s ease forwards`};
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 480px;
  width: 92%;
  animation: ${css`${slideUp} 0.55s cubic-bezier(0.22,1,0.36,1) forwards`};
`;

const Logo = styled.img`
  display: block;
  width: 96px;
  margin: 0 auto 34px;
  opacity: 0.9;
`;

// Hairline divider
const Rule = styled.div`
  width: 48px;
  height: 1px;
  background: rgba(16,29,64,0.15);
  margin: 0 auto 28px;
`;

// ─── High demand badge ────────────────────────────────────────────────────────

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 10px 22px;
  border: 1px solid rgba(185, 64, 64, 0.28);
  border-radius: 2px;
  background: rgba(185, 64, 64, 0.06);
  margin-bottom: 26px;
`;

const BadgeDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${RED};
  flex-shrink: 0;
  animation: ${dotPulse} 1.4s ease-in-out infinite;
`;

const BadgeText = styled.span`
  font-family: 'Cormorant Garamond', serif;
  font-size: 0.82rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${RED};
`;

// ─── SVG walking figures ──────────────────────────────────────────────────────

const FigureRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 14px;
  margin-bottom: 34px;
  min-height: 72px;
`;

// Animated SVG sub-elements
const AnimBodyG = styled.g`
  animation: ${bodyBob} 0.5s ease-in-out infinite;
  transform-origin: 10px 16px;
`;
const AnimLegL = styled.line`
  animation: ${legFwd} 0.5s ease-in-out infinite;
  transform-origin: 10px 26px;
`;
const AnimLegR = styled.line`
  animation: ${legBck} 0.5s ease-in-out infinite;
  transform-origin: 10px 26px;
`;
const AnimArmL = styled.line`
  animation: ${armFwd} 0.5s ease-in-out infinite;
  transform-origin: 10px 17px;
`;
const AnimArmR = styled.line`
  animation: ${armBck} 0.5s ease-in-out infinite;
  transform-origin: 10px 17px;
`;

function WalkFigure({ isYou }) {
  const c  = isYou ? NAVY : '#c8cdd8';
  const sw = isYou ? 2   : 1.55;
  const sc = isYou ? 1.48 : 1.08;

  return (
    <svg
      width={20 * sc}
      height={40 * sc}
      viewBox="0 0 20 40"
      style={{ overflow: 'visible' }}
    >
      {/* Static ghost figures */}
      {!isYou && (
        <>
          <circle cx="10" cy="5"  r="3.5" fill={c} />
          <line x1="10" y1="9"  x2="10" y2="26" stroke={c} strokeWidth={sw} />
          <line x1="10" y1="17" x2="3"  y2="22" stroke={c} strokeWidth={sw} strokeLinecap="round" />
          <line x1="10" y1="17" x2="17" y2="22" stroke={c} strokeWidth={sw} strokeLinecap="round" />
          <line x1="10" y1="26" x2="5"  y2="38" stroke={c} strokeWidth={sw} strokeLinecap="round" />
          <line x1="10" y1="26" x2="15" y2="38" stroke={c} strokeWidth={sw} strokeLinecap="round" />
        </>
      )}

      {/* Animated "you" figure */}
      {isYou && (
        <>
          <circle cx="10" cy="5" r="3.8" fill={c} />
          <AnimBodyG>
            <line x1="10" y1="9"  x2="10" y2="26" stroke={c} strokeWidth={sw} />
            <AnimArmL x1="10" y1="17" x2="2.5" y2="23" stroke={c} strokeWidth={sw} strokeLinecap="round" />
            <AnimArmR x1="10" y1="17" x2="17.5" y2="23" stroke={c} strokeWidth={sw} strokeLinecap="round" />
            <AnimLegL x1="10" y1="26" x2="4" y2="38" stroke={c} strokeWidth={sw} strokeLinecap="round" />
            <AnimLegR x1="10" y1="26" x2="16" y2="38" stroke={c} strokeWidth={sw} strokeLinecap="round" />
          </AnimBodyG>
        </>
      )}
    </svg>
  );
}

// ─── Queue position text ──────────────────────────────────────────────────────

const QueueLabel = styled.p`
  font-family: 'Cormorant Garamond', serif;
  font-size: 0.78rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${BRONZE};
  margin: 0 0 8px;
`;

const QueueNumber = styled.div`
  font-family: 'Cormorant Garamond', serif;
  font-size: clamp(4rem, 13vw, 5.8rem);
  font-weight: 600;
  color: ${NAVY};
  line-height: 1;
  margin-bottom: 8px;
  letter-spacing: -0.01em;
`;

const QueueSub = styled.p`
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.15rem;
  color: rgba(16,29,64,0.48);
  margin: 0 0 30px;
  font-style: italic;
`;

// Progress line
const LineTrack = styled.div`
  width: 100%;
  height: 2px;
  background: rgba(16,29,64,0.12);
  overflow: hidden;
  margin-bottom: 18px;
`;
const LineFill = styled.div`
  height: 100%;
  background: ${BRONZE};
  animation: ${({ $dur }) => css`${lineFill} ${$dur}s linear forwards`};
`;

const StatusText = styled.p`
  font-family: 'Cormorant Garamond', serif;
  font-size: 0.82rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(16,29,64,0.42);
  margin: 0;
  animation: ${softPulse} 2.2s ease-in-out infinite;
`;

const ExplainerText = styled.p`
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.08rem;
  font-style: italic;
  color: rgba(16,29,64,0.52);
  line-height: 1.7;
  margin: 0 0 30px;
`;

// ─── Success / timer ──────────────────────────────────────────────────────────

const SuccessCard = styled(Card)`
  animation: ${css`${popIn} 0.45s cubic-bezier(0.34,1.56,0.64,1) forwards`};
`;

const CheckCircle = styled.div`
  width: 62px;
  height: 62px;
  border-radius: 50%;
  border: 1.5px solid ${BRONZE};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 22px;
`;
const CheckSVG = styled.svg`
  polyline {
    stroke: ${BRONZE};
    stroke-width: 2.25;
    stroke-linecap: round;
    stroke-linejoin: round;
    fill: none;
    stroke-dasharray: 60;
    stroke-dashoffset: 60;
    animation: ${checkDraw} 0.5s ease 0.2s forwards;
  }
`;

const SuccessHeadline = styled.h2`
  font-family: 'Cormorant Garamond', serif;
  font-size: clamp(1.85rem, 5.5vw, 2.45rem);
  font-weight: 600;
  color: ${NAVY};
  margin: 0 0 10px;
  letter-spacing: 0.01em;
`;

const SuccessSub = styled.p`
  font-family: 'Cormorant Garamond', serif;
  font-size: 1.12rem;
  color: rgba(16,29,64,0.54);
  margin: 0 0 26px;
  font-style: italic;
  line-height: 1.75;
`;

const TimerBox = styled.div`
  width: 100%;
  border: 1px solid rgba(185,64,64,0.22);
  border-radius: 2px;
  padding: 18px 22px 16px;
  margin-bottom: 24px;
  background: rgba(185,64,64,0.04);
`;
const TimerLabel = styled.p`
  font-family: 'Cormorant Garamond', serif;
  font-size: 0.76rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(185,64,64,0.72);
  margin: 0 0 8px;
`;
const TimerCount = styled.div`
  font-family: 'Cormorant Garamond', serif;
  font-size: 3.15rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  color: ${RED};
  line-height: 1;
  animation: ${({ $urgent }) => $urgent ? css`${timerUrgent} 0.8s ease-in-out infinite` : 'none'};
`;
const TimerSub = styled.p`
  font-family: 'Cormorant Garamond', serif;
  font-size: 0.92rem;
  color: rgba(185,64,64,0.58);
  margin: 8px 0 0;
  font-style: italic;
`;

const ProceedButton = styled.button`
  width: 100%;
  background: ${NAVY};
  border: none;
  border-radius: 2px;
  color: ${CREAM};
  font-family: 'Cormorant Garamond', serif;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  padding: 18px 44px;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.15s ease;
  &:hover {
    background: #192e5c;
    transform: translateY(-1px);
  }
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatTime(s) {
  return `${String(Math.floor(s / 60)).padStart(2,'0')}:${String(s % 60).padStart(2,'0')}`;
}

// ─── Component ────────────────────────────────────────────────────────────────

const QueueScreen = ({ onComplete, totalMs = 7000 }) => {
  const HOLD_SECONDS = 600;
  const initialPos   = useRef(Math.floor(Math.random() * 14) + 9);
  const [pos, setPos]           = useState(initialPos.current);
  const [phase, setPhase]       = useState('queue');
  const [countdown, setCountdown] = useState(HOLD_SECONDS);
  const posRef = useRef(initialPos.current);
  const TOTAL  = 6;

  useEffect(() => {
    if (phase !== 'queue') return;
    const endAt = Date.now() + totalMs - 800;
    const iv = setInterval(() => {
      const rem = endAt - Date.now();
      if (rem <= 0) { setPos(0); setPhase('success'); clearInterval(iv); return; }
      const pct    = 1 - rem / (totalMs - 800);
      const newPos = Math.max(1, Math.round(initialPos.current * (1 - Math.pow(pct, 0.6))));
      if (newPos !== posRef.current) { posRef.current = newPos; setPos(newPos); }
    }, 180);
    return () => clearInterval(iv);
  }, [phase, totalMs]);

  useEffect(() => {
    if (phase !== 'success') return;
    const iv = setInterval(() => setCountdown(c => c > 0 ? c - 1 : 0), 1000);
    return () => clearInterval(iv);
  }, [phase]);

  useEffect(() => {
    if (phase !== 'success') return;
    const t = setTimeout(() => { setPhase('leaving'); setTimeout(onComplete, 530); }, 2200);
    return () => clearTimeout(t);
  }, [phase, onComplete]);

  if (phase === 'success' || phase === 'leaving') {
    return (
      <Overlay $leaving={phase === 'leaving'}>
        <SuccessCard>
          <Logo src={navyLogo} alt="Belvoir Castle" />
          <Rule />
          <CheckCircle>
            <CheckSVG viewBox="0 0 24 24" width="28" height="28">
              <polyline points="5,13 10,18 19,7" />
            </CheckSVG>
          </CheckCircle>
          <SuccessHeadline>Your place is ready</SuccessHeadline>
          <SuccessSub>
            Complete your booking before your spot<br />is released back to the queue.
          </SuccessSub>
          <TimerBox>
            <TimerLabel>Time remaining to complete your booking</TimerLabel>
            <TimerCount $urgent={countdown < 120}>{formatTime(countdown)}</TimerCount>
            <TimerSub>Your queue place will be cleared after this time</TimerSub>
          </TimerBox>
          <ProceedButton onClick={() => { setPhase('leaving'); setTimeout(onComplete, 530); }}>
            Continue to Booking
          </ProceedButton>
        </SuccessCard>
      </Overlay>
    );
  }

  return (
    <Overlay $leaving={false}>
      <Card>
        <Logo src={navyLogo} alt="Belvoir Castle" />
        <Rule />

        <Badge>
          <BadgeDot />
          <BadgeText>This event is in high demand</BadgeText>
        </Badge>

        <ExplainerText>
          Due to exceptional demand for this event, we've introduced a short virtual queue
          to ensure everyone has a fair chance to book.
        </ExplainerText>

        <FigureRow>
          {Array.from({ length: TOTAL }).map((_, i) => (
            <WalkFigure key={i} isYou={i === TOTAL - 1} />
          ))}
        </FigureRow>

        <QueueLabel>Your Queue Position</QueueLabel>
        <QueueNumber>{pos}</QueueNumber>
        <QueueSub>guests ahead of you</QueueSub>

        <LineTrack>
          <LineFill $dur={(totalMs / 1000).toFixed(1)} />
        </LineTrack>

        <StatusText>Securing your availability…</StatusText>
      </Card>
    </Overlay>
  );
};

export default QueueScreen;
